import React, { useEffect, useState } from 'react';
import {
  View, Text, FlatList, TouchableOpacity, StyleSheet, ActivityIndicator,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RunStackParamList } from '../../navigation/AppNavigator';
import { runsApi } from '../../api';
import type { Run } from '../../types';
import { paceMinsPerMile } from '../../utils/geo';

type Props = NativeStackScreenProps<RunStackParamList, 'RunHistory'>;

function formatDuration(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

export default function RunHistoryScreen({ navigation }: Props) {
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    runsApi.list().then(setRuns).finally(() => setLoading(false));
  }, []);

  if (loading) return <ActivityIndicator style={{ flex: 1 }} size="large" color="#1a73e8" />;

  return (
    <View style={styles.container}>
      <FlatList
        data={runs.sort((a, b) => b.startedAt.localeCompare(a.startedAt))}
        keyExtractor={(i) => i.runId}
        ListEmptyComponent={<Text style={styles.empty}>No runs yet — start your first run!</Text>}
        renderItem={({ item }) => {
          const pace = item.totalDurationSecs > 0 && item.totalDistanceMiles > 0
            ? paceMinsPerMile(item.totalDistanceMiles, item.totalDurationSecs).toFixed(2)
            : '—';
          return (
            <TouchableOpacity style={styles.item} onPress={() => navigation.navigate('RunDetail', { runId: item.runId })}>
              <View style={styles.dateCol}>
                <Text style={styles.date}>{new Date(item.startedAt).toLocaleDateString()}</Text>
                <Text style={styles.time}>{new Date(item.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
              </View>
              <View style={styles.statsCol}>
                <Text style={styles.dist}>{item.totalDistanceMiles.toFixed(2)} mi</Text>
                <Text style={styles.dur}>{formatDuration(item.totalDurationSecs)}</Text>
              </View>
              <View style={styles.paceCol}>
                <Text style={styles.paceVal}>{pace}</Text>
                <Text style={styles.paceLabel}>min/mi</Text>
              </View>
            </TouchableOpacity>
          );
        }}
        ItemSeparatorComponent={() => <View style={styles.sep} />}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  item: { flexDirection: 'row', padding: 16, alignItems: 'center' },
  dateCol: { width: 80 },
  date: { fontWeight: '600', fontSize: 13 },
  time: { color: '#888', fontSize: 11 },
  statsCol: { flex: 1, paddingLeft: 8 },
  dist: { fontSize: 16, fontWeight: '700', color: '#1a73e8' },
  dur: { color: '#555', fontSize: 13 },
  paceCol: { alignItems: 'flex-end' },
  paceVal: { fontSize: 18, fontWeight: '700' },
  paceLabel: { color: '#888', fontSize: 11 },
  sep: { height: 1, backgroundColor: '#f0f0f0' },
  empty: { textAlign: 'center', color: '#aaa', marginTop: 60, paddingHorizontal: 32 },
});
